// One Stage — Conversation V2 feature-flag reader.
//
// Returns a reactive boolean indicating whether the experimental
// MessagesViewNext path should be used in place of the legacy MessagesView.
//
// Activation sources (checked in order, first truthy wins):
//   1. window.bottle.conversationV2  — runtime override for QA / staging
//   2. localStorage 'conversation_v2' === 'true' — per-operator opt-in
//   3. window.chatwootConfig.conversationV2  — instance-wide rollout flag
//
// This composable is consumed at the conversation-view boundary
// (e.g. ConversationView.vue) to decide which shell to mount. It is
// intentionally cheap: a single ref, refreshed on window focus so that
// operators can flip the flag in DevTools without a full reload.
//
// Reverting is one localStorage delete away.
//
// Usage:
//   import { useConversationV2Flag } from 'dashboard/composables/useConversationV2Flag';
//   const { isV2Enabled, setV2Enabled } = useConversationV2Flag();
//   <MessagesView v-if="!isV2Enabled" /><MessagesViewNext v-else />

import { onMounted, onUnmounted, ref } from 'vue';

const STORAGE_KEY = 'conversation_v2';

const readFlag = () => {
  // 1. Runtime override (highest priority — QA / DevTools toggling).
  if (typeof window !== 'undefined' && window.bottle?.conversationV2 != null) {
    return Boolean(window.bottle.conversationV2);
  }

  // 2. Per-operator opt-in via localStorage.
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch (e) {
    // localStorage unavailable (Safari private mode, etc.) — fall through.
  }

  // 3. Instance-wide config flag (default rollout vector).
  if (typeof window !== 'undefined' && window.chatwootConfig?.conversationV2) {
    return Boolean(window.chatwootConfig.conversationV2);
  }

  return false;
};

export function useConversationV2Flag() {
  const isV2Enabled = ref(readFlag());

  // Re-check the flag when the tab regains focus — this lets operators flip
  // the flag in DevTools or another tab without a full page reload.
  const refresh = () => {
    const next = readFlag();
    if (next !== isV2Enabled.value) {
      isV2Enabled.value = next;
    }
  };

  onMounted(() => {
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', refresh);
  });

  onUnmounted(() => {
    window.removeEventListener('focus', refresh);
    window.removeEventListener('storage', refresh);
  });

  const setV2Enabled = value => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, value ? 'true' : 'false');
    } catch (e) {
      // ignore
    }
    isV2Enabled.value = Boolean(value);
  };

  const resetV2Flag = () => {
    try {
      window.localStorage?.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    isV2Enabled.value = readFlag();
  };

  return {
    isV2Enabled,
    setV2Enabled,
    resetV2Flag,
    refresh,
  };
}

// Exposed for tests and for non-composition-API call sites.
export const conversationV2Enabled = readFlag;
